import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { isRedoEmailAddress } from "@redotech/redo-model/redo-domains";
import { RedoFeatureNotificationCard } from "@redotech/redo-web/arbiter-components/feature-notification/redo-feature-notification-card";
import { memo, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import * as styles from "./catalog-nav-announcement-popup.module.css";

const LOCAL_STORAGE_KEY = "redo.catalog-nav-announcement-dismissed";
const CATALOG_ANNOUNCEMENT_IMAGE =
  "https://assets.getredo.com/catalog/catalog-nav-announcement.png";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
  } catch {
    // localStorage unavailable; fall back to in-memory dismissal.
  }
}

export const CatalogNavAnnouncementPopup = memo(
  function CatalogNavAnnouncementPopup() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const team = useContext(TeamContext);
    const user = useContext(UserContext);
    const { pathname, search } = useLocation();
    const isPreview = new URLSearchParams(search).get(
      "catalogAnnouncementPreview",
    );

    const [dismissed, setDismissed] = useState(() =>
      isPreview ? false : readDismissed(),
    );

    const handleDismiss = useHandler(() => {
      setDismissed(true);
      writeDismissed();
    });

    if (dismissed) {
      return null;
    }
    if (!isPreview && isRedoEmailAddress(user?.email)) {
      return null;
    }
    if (!isPreview && !team?.settings.ims?.catalogEnabled) {
      return null;
    }
    if (
      !isPreview &&
      !pathname.startsWith(routes.inventoryManagementBaseUrl) &&
      !pathname.startsWith(routes.catalogBaseUrl)
    ) {
      return null;
    }

    return (
      <div className={styles.container}>
        <RedoFeatureNotificationCard
          actionText="Dismiss"
          description="Manage product records, variants, and listings in Catalog, now accessible from the main navigation."
          image={CATALOG_ANNOUNCEMENT_IMAGE}
          onAction={handleDismiss}
          onDismiss={handleDismiss}
          title="Catalog has moved"
        />
      </div>
    );
  },
);
